import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Activity,
  Download,
  RefreshCw,
  Sparkles,
  AlertTriangle,
  ExternalLink,
  Calendar,
} from "lucide-react";
import {
  listGpsReports,
  getGpsReportDownloadUrl,
  type GpsReportListItem,
} from "@/utils/reports.functions";
import { triggerGpsReport } from "@/utils/gps-report.functions";
import { useLocaleFormat } from "@/lib/i18n-format";
import { PdfPreviewDialog } from "@/components/reports/PdfPreviewDialog";
import { downloadPdfFromBase64 } from "@/lib/report-export";

export const Route = createFileRoute("/_app/reports/gps")({
  component: GpsReportsPage,
});

function GpsReportsPage() {
  const { t } = useTranslation();
  const { formatDateTime, formatDate } = useLocaleFormat();
  const [reports, setReports] = useState<GpsReportListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewBase64, setPdfPreviewBase64] = useState<string | null>(null);
  const [pdfPreviewFilename, setPdfPreviewFilename] = useState<string>("");

  const listFn = useServerFn(listGpsReports);
  const downloadFn = useServerFn(getGpsReportDownloadUrl);
  const triggerFn = useServerFn(triggerGpsReport);

  const load = async () => {
    setLoading(true);
    try {
      const list = await listFn();
      setReports(list);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("pages.reports.common.loadError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await triggerFn();
      toast.success(t("pages.reports.gps.generateSuccess"));
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("pages.reports.gps.generateError"));
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async (report: GpsReportListItem) => {
    setDownloadingId(report.id);
    try {
      const { url } = await downloadFn({ data: { reportId: report.id } });
      // Fetch the PDF and convert to base64 to show in the in-app preview
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const buf = await res.arrayBuffer();
      const bytes = new Uint8Array(buf);
      // Convert in chunks to avoid stack overflow on large PDFs
      let binary = "";
      const chunkSize = 0x8000;
      for (let i = 0; i < bytes.length; i += chunkSize) {
        binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunkSize)));
      }
      const base64 = btoa(binary);
      const filename = `gps-report-${report.periodStart}_${report.periodEnd}.pdf`;
      setPdfPreviewBase64(base64);
      setPdfPreviewFilename(filename);
      setPdfPreviewOpen(true);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("pages.reports.gps.downloadError"));
    } finally {
      setDownloadingId(null);
    }
  };

  const confirmDownloadPdf = () => {
    if (!pdfPreviewBase64) return;
    downloadPdfFromBase64(pdfPreviewFilename, pdfPreviewBase64);
    setPdfPreviewOpen(false);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-primary" />
              {t("pages.reports.gps.title")}
            </CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">{t("pages.reports.gps.subtitle")}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={loading}>
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              {t("pages.reports.common.refresh")}
            </Button>
            <Button size="sm" onClick={handleGenerate} disabled={generating}>
              {generating ? (
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="mr-2 h-4 w-4" />
              )}
              {generating ? t("pages.reports.gps.generating") : t("pages.reports.gps.generateNow")}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : reports.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border bg-muted/30 p-8 text-center">
              <Activity className="mx-auto mb-3 h-10 w-10 text-muted-foreground/40" />
              <p className="text-sm font-medium">{t("pages.reports.gps.emptyTitle")}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                {t("pages.reports.gps.emptyHint")}
              </p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-lg border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("pages.reports.gps.tableHeaders.period")}</TableHead>
                    <TableHead>{t("pages.reports.gps.tableHeaders.generatedAt")}</TableHead>
                    <TableHead className="text-right">
                      {t("pages.reports.gps.tableHeaders.attempts")}
                    </TableHead>
                    <TableHead className="text-right">
                      {t("pages.reports.gps.tableHeaders.outOfRadius")}
                    </TableHead>
                    <TableHead className="w-32 text-right">
                      {t("pages.reports.gps.tableHeaders.actions")}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reports.map((r) => {
                    const violations = r.outOfRadiusCount;
                    const pct =
                      r.totalAttempts > 0
                        ? ((violations / r.totalAttempts) * 100).toFixed(1)
                        : "0.0";
                    return (
                      <TableRow key={r.id}>
                        <TableCell>
                          <div className="flex items-center gap-2 text-sm">
                            <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                            {formatDate(r.periodStart)} → {formatDate(r.periodEnd)}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDateTime(r.generatedAt)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{r.totalAttempts}</TableCell>
                        <TableCell className="text-right">
                          {violations > 0 ? (
                            <Badge variant="destructive" className="gap-1">
                              <AlertTriangle className="h-3 w-3" />
                              {violations} ({pct}%)
                            </Badge>
                          ) : (
                            <Badge variant="secondary">0</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDownload(r)}
                            disabled={downloadingId === r.id}
                          >
                            <Download className="mr-1.5 h-3.5 w-3.5" />
                            PDF
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex items-center justify-between gap-4 p-4">
          <div>
            <p className="text-sm font-medium">{t("pages.reports.gps.interactiveTitle")}</p>
            <p className="text-xs text-muted-foreground">
              {t("pages.reports.gps.interactiveSubtitle")}
            </p>
          </div>
          <Button variant="outline" size="sm" asChild>
            <Link to="/gps-analytics">
              {t("pages.reports.gps.openAnalytics")}
              <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
            </Link>
          </Button>
        </CardContent>
      </Card>

      <PdfPreviewDialog
        open={pdfPreviewOpen}
        onOpenChange={setPdfPreviewOpen}
        pdfBase64={pdfPreviewBase64}
        filename={pdfPreviewFilename}
        onDownload={confirmDownloadPdf}
      />
    </div>
  );
}
